import React, { useState, useEffect, ChangeEvent } from 'react';
import axios from 'axios';
import {
    AppBar, Toolbar, Typography, Container, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, IconButton, Button,
    TextField, Box, Card, CardContent, Divider, Avatar, Tooltip, Link
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    Delete, Block, CloudUpload, Send, Logout, Notifications,
    People, Comment as CommentIcon, BarChart, History,
    Copyright, Favorite, Link as LinkIcon
} from '@mui/icons-material';
import { Subscriber, Comment, AuthProps } from '../types';
import jsPDF from "jspdf";
import MediaUploader from "../components/MediaUploader";

interface AppNotification {
    notification_id: string;
    title: string;
    body: string;
    image_url: string | null;
    click_count: number;
    created_at: string;
}

// Structural mapping matching your newly altered phpMyAdmin database table schema fields
interface UploadedMedia {
    media_id: string;
    file_path: string; // The physical asset backdrop background
    redirect_url: string | null; // The clickable tracking website landing page action
    mime_type: string;
    lifetime_minutes: number | null;
    created_at: string;
}

const AdminPage: React.FC<AuthProps> = ({ setAuth }) => {
    const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
    const [comments, setComments] = useState<Comment[]>([]);
    const [history, setHistory] = useState<AppNotification[]>([]);

    // Persistent live registry tracker array
    const [mediaItems, setMediaItems] = useState<UploadedMedia[]>([]);

    const [title, setTitle] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [image, setImage] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [previewCover, setPreviewCover] = useState<string | null>(null);
    const [fileType, setFileType] = useState<string | null>(null);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [IsUploadingMedia, setIsUploadingMedia] = useState<boolean>(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const subRes = await axios.get<Subscriber[]>('https://api.237showbiz.com/api/subscribers');
            const comRes = await axios.get<Comment[]>('https://api.237showbiz.com/api/comments');
            const notifRes = await axios.get<{notifications: AppNotification[]}>(
                'https://api.237showbiz.com/api/Notifications?subscriber_id=admin'
            );

            // Fetch current media registry items from backend controller
            const mediaRes = await axios.get<UploadedMedia[]>('https://api.237showbiz.com/api/media');

            setSubscribers(subRes.data || []);
            setComments(comRes.data || []);
            setHistory(notifRes.data.notifications || []);

            // Synchronize local states with explicit backend verification checks
            setMediaItems(Array.isArray(mediaRes.data) ? mediaRes.data : []);
        } catch (err) {
            console.error("Dashboard Fetch Registry Sync Error:", err);
        }
    };

    const handleCoverImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // This is the critical line that was likely missing or disconnected
            setImage(file);

            // Update technical metadata for the preview
            setFileType(file.type);
            setPreviewCover(URL.createObjectURL(file));

            console.log("File captured for Split-Advert:", file.name);
        }
    };

    const handleClearMedia = () => {
        if (previewCover) {
            URL.revokeObjectURL(previewCover);
        }
        setImage(null);
        setFileType(null);
        setPreviewCover(null);
    };

    // Split-Advert Media Form Submission Handler Function
    // Staging or temporary verification callback for the media component's button
    const handleMediaSubmitAction = async (
        payload: {
            mediaUrl: string | null;
            lifetimeMinutes: number | null;
        }
    ) => {

        console.log("=== SUBMIT START ===");

        console.log("Current image object:", image);

        console.log("Current redirect URL:", payload.mediaUrl);

        if (!image && !payload.mediaUrl) {

            alert(
                "Please upload a file or provide a redirect URL."
            );

            return;
        }

        setIsUploadingMedia(true);

        try {

            const formData = new FormData();

            // =========================
            // FILE
            // =========================

            if (image instanceof File) {

                console.log("Appending FILE:", image.name);

                formData.append(
                    "image",
                    image
                );
            }

            // =========================
            // REDIRECT URL
            // =========================

            if (
                payload.mediaUrl &&
                payload.mediaUrl.trim() !== ""
            ) {

                formData.append(
                    "redirect_url",
                    payload.mediaUrl.trim()
                );
            }

            // =========================
            // LIFETIME
            // =========================

            if (
                payload.lifetimeMinutes !== null
            ) {

                formData.append(
                    "lifetime_minutes",
                    payload.lifetimeMinutes.toString()
                );
            }

            // =========================
            // DEBUG FORMDATA
            // =========================

            console.log("=== FORMDATA ===");

            formData.forEach((value, key) => {

                console.log(key, value);

            });

            // =========================
            // REQUEST
            // =========================

            const response = await axios.post(
                "https://api.237showbiz.com/api/media",
                formData,
                {
                    headers: {
                        "Content-Type":
                            "multipart/form-data",
                    },
                }
            );

            console.log(
                "SERVER RESPONSE:",
                response.data
            );

            if (response.data.success) {

                alert(
                    "✅ Advert uploaded successfully!"
                );

                fetchData();

            } else {

                alert(
                    response.data.error ||
                    "Upload failed."
                );
            }

        } catch (error: any) {

            console.error(error);

            alert(
                error?.response?.data?.error ||
                error.message
            );

        } finally {

            setIsUploadingMedia(false);
        }
    };

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const sendNotification = async () => {
        if (!title.trim() || !body.trim()) {
            return alert("Please enter both a Title and a Message Body.");
        }

        setIsSending(true);

        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('body', body);

            if (image) {
                formData.append('image', image);
            }

            const res = await axios.post(
                'https://api.237showbiz.com/api/Notifications?action=send',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (res.data.success) {
                alert("✅ Notification Broadcasted Successfully!");
                setTitle('');
                setBody('');
                setImage(null);
                setPreview(null);
                fetchData();
            } else {
                alert("Server Error: " + (res.data.error || "Failed to send notification."));
            }
        } catch (err) {
            console.error("Broadcast error:", err);
            alert("❌ Network Error: Could not connect to the server.");
        } finally {
            setIsSending(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('isAdmin');
        setAuth(false);
    };

    const downloadNotificationPDF = (notif: AppNotification) => {
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text('237Showbiz - Notification Report', 14, 20);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
        doc.setDrawColor(200);
        doc.line(14, 32, 196, 32);
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text('Notification Details', 14, 42);
        doc.setFontSize(11);
        doc.text(`Title: ${notif.title}`, 14, 52);
        doc.text(`Message:`, 14, 62);
        doc.setFontSize(10);
        doc.text(notif.body, 14, 70, { maxWidth: 180 });
        doc.setFontSize(11);
        doc.text(`Clicks: ${notif.click_count}`, 14, 100);
        doc.text(`Sent At: ${new Date(notif.created_at).toLocaleString()}`, 14, 108);
        doc.setFontSize(9);
        doc.setTextColor(150);
        doc.text('© 237Showbiz Admin System', 14, 285);
        doc.save(`notification-${notif.notification_id}.pdf`);
    };

    const poppins = { fontFamily: "'Poppins', sans-serif" };

    return (
        <Box sx={{ flexGrow: 1, bgcolor: '#F4F7FE', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* STICKY HEADER NAVBAR */}
            <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #E9EDF7', zIndex: 1100 }}>
                <Toolbar sx={{ py: 1 }}>
                    <Avatar sx={{ bgcolor: 'orange', mr: 2, boxShadow: '0 4px 12px rgba(255,165,0,0.4)' }}>
                        <Notifications />
                    </Avatar>
                    <Typography variant="h6" sx={{ flexGrow: 1, color: '#1B2559', fontWeight: 700, ...poppins }}>
                        237Showbiz <Box component="span" sx={{ color: 'orange', fontWeight: 400 }}>Admin Center</Box>
                    </Typography>
                    <Button
                        onClick={handleLogout}
                        variant="contained"
                        disableElevation
                        color="error"
                        startIcon={<Logout />}
                        sx={{ borderRadius: '12px', textTransform: 'none', px: 3, fontWeight: 600, ...poppins }}
                    >
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            {/* MAIN CONTENT CONTAINMENT VIEWPORT */}
            <Container maxWidth="lg" sx={{ mt: 6, flex: 1 }}>
                <Grid container spacing={4}>
                    {/* DISPATCH PUSH BROADCAST FORM PANEL */}
                    <Grid size={{ xs: 12 }}>
                        <Card elevation={0} sx={{ borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', border: '1px solid #FFF' }}>
                            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ p: 1.5, bgcolor: '#FFF7ED', borderRadius: '12px' }}>
                                        <Send sx={{ color: 'orange' }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1B2559', mb: 0.5, ...poppins }}>
                                            Push Broadcast
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#A3AED0', ...poppins }}>
                                            Send instant notifications with real-time tracking.
                                        </Typography>
                                    </Box>
                                </Box>

                                <Grid container spacing={4}>
                                    <Grid size={{ xs: 12, md: 7 }}>
                                        <TextField
                                            fullWidth label="Notification Title" variant="filled" sx={{ mb: 3 }}
                                            value={title} onChange={(e) => setTitle(e.target.value)}
                                            InputProps={{ disableUnderline: true, sx: { borderRadius: '15px', bgcolor: '#F4F7FE', ...poppins } }}
                                        />
                                        <TextField
                                            fullWidth label="Message Body" variant="filled" multiline rows={5}
                                            value={body} onChange={(e) => setBody(e.target.value)}
                                            InputProps={{ disableUnderline: true, sx: { borderRadius: '15px', bgcolor: '#F4F7FE', ...poppins } }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 5 }}>
                                        <Box sx={{
                                            border: '2px dashed #E0E5F2', borderRadius: '20px', height: '100%',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            justifyContent: 'center', p: 3, bgcolor: '#F4F7FE', minHeight: 240,
                                            transition: 'all 0.3s ease', '&:hover': { borderColor: 'orange', bgcolor: '#FFF' }
                                        }}>
                                            {!preview ? (
                                                <Button variant="text" component="label" startIcon={<CloudUpload />} sx={{ ...poppins, textTransform: 'none', color: '#707EAE', fontWeight: 600 }}>
                                                    Upload Media Asset
                                                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                                </Button>
                                            ) : (
                                                <Box textAlign="center" sx={{ width: '100%' }}>
                                                    <img src={preview} alt="preview" style={{ width: '100%', maxHeight: 180, borderRadius: '16px', objectFit: 'contain' }} />
                                                    <Button size="small" color="error" variant="text" onClick={() => {setImage(null); setPreview(null);}} sx={{ mt: 1, fontWeight: 600, ...poppins }}>
                                                        Remove Media
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Button
                                    fullWidth variant="contained" size="large" startIcon={<Send />}
                                    disabled={isSending} onClick={sendNotification}
                                    sx={{
                                        mt: 10, bgcolor: 'orange', py: 2, borderRadius: '15px',
                                        fontWeight: 700, fontSize: '1rem', textTransform: 'none',
                                        boxShadow: '0 12px 24px rgba(255, 165, 0, 0.3)',
                                        '&:hover': { bgcolor: '#FB923C' },
                                        ...poppins
                                    }}
                                >
                                    {isSending ? 'Dispatching...' : 'Dispatch Broadcast'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* SPLIT SLIDER MEDIA UPLOADER COMPONENT MODULE AREA */}
                    <Grid size={{ xs: 12 }}>
                        <MediaUploader
                            preview={previewCover}
                            fileType={fileType}
                            onImageChange={handleCoverImageChange}
                            onClearMedia={handleClearMedia}
                            onSubmitMedia={handleMediaSubmitAction}
                            poppins={poppins}
                            isUploadingMedia={IsUploadingMedia}
                        />
                    </Grid>

                    {/* LIVE TRACKED RESTRUCTURED MEDIA LOGS DATABASE REGISTRY TABLE VIEW */}
                    <Grid size={{ xs: 12 }}>
                        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white' }}>
                            <Box p={4} display="flex" alignItems="center" gap={2}>
                                <Box sx={{ p: 1, bgcolor: '#F0F9FF', borderRadius: '10px' }}>
                                    <LinkIcon sx={{ color: '#0EA5E9' }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>Live Media Link Registry</Typography>
                            </Box>
                            <Divider />
                            <TableContainer sx={{ maxHeight: 320 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>MEDIA ID</TableCell>
                                            <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>BACKGROUND ASSET SOURCE</TableCell>
                                            <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>ACTION REDIRECT LINK</TableCell>
                                            <TableCell align="center" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>TYPE</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {mediaItems.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#A3AED0', ...poppins }}>
                                                    No tracked media logs loaded in DB.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            mediaItems.map((item) => (
                                                <TableRow key={item.media_id} hover>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1B2559', ...poppins }}>
                                                        {item.media_id.substring(0, 8)}...
                                                    </TableCell>

                                                    {/* UPDATED COLUMN: Preview Image or Video */}
                                                    <TableCell sx={poppins}>
                                                        <Box sx={{ width: 80, height: 60, borderRadius: '8px', overflow: 'hidden', bgcolor: '#F4F7FE' }}>
                                                            {item.mime_type.startsWith('image/') ? (
                                                                <img
                                                                    src={item.file_path}
                                                                    alt="media"
                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                />
                                                            ) : item.mime_type.startsWith('video/') ? (
                                                                <video
                                                                    src={item.file_path}
                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                    muted
                                                                />
                                                            ) : (
                                                                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                                                    <CloudUpload sx={{ color: '#A3AED0' }} />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        <Link href={item.file_path} target="_blank" rel="noreferrer" sx={{ display: 'block', mt: 1, fontSize: '0.7rem', color: 'orange', textDecoration: 'none' }}>
                                                            View Full File
                                                        </Link>
                                                    </TableCell>

                                                    {/* ACTION REDIRECT LINK */}
                                                    <TableCell sx={poppins}>
                                                        {item.redirect_url ? (
                                                            <Link href={item.redirect_url} target="_blank" rel="noreferrer" sx={{ color: '#0EA5E9', fontWeight: 600, textDecoration: 'none' }}>
                                                                {item.redirect_url.length > 30 ? `${item.redirect_url.substring(0, 30)}...` : item.redirect_url}
                                                            </Link>
                                                        ) : (
                                                            <Typography variant="caption" sx={{ color: '#A3AED0', fontStyle: 'italic' }}>None</Typography>
                                                        )}
                                                    </TableCell>

                                                    <TableCell align="center" sx={{ ...poppins, fontSize: '0.8rem', color: '#707EAE', fontWeight: 600 }}>
                                                        {item.mime_type}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* HISTORY TABLE TRACKING SECTION */}
                    <Grid size={{ xs: 12 }}>
                        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white' }}>
                            <Box p={4} display="flex" alignItems="center" gap={2}>
                                <Box sx={{ p: 1, bgcolor: '#F0F9FF', borderRadius: '10px' }}>
                                    <History sx={{ color: '#0EA5E9' }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>Broadcast History (Last 48h)</Typography>
                            </Box>
                            <Divider />
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>NOTIFICATION</TableCell>
                                            <TableCell align="center" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>CLICKS</TableCell>
                                            <TableCell align="center" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>SENT AT</TableCell>
                                            <TableCell align="right" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>ACTION</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {history.length === 0 ? (
                                            <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: '#A3AED0', ...poppins }}>No recent broadcasts found.</TableCell></TableRow>
                                        ) : (
                                            history.map((notif) => (
                                                <TableRow key={notif.notification_id} hover>
                                                    <TableCell sx={poppins}>
                                                        <Box display="flex" alignItems="center" gap={2}>
                                                            {notif.image_url ? (
                                                                <Avatar src={notif.image_url} variant="rounded" sx={{ width: 45, height: 45, borderRadius: '10px' }} />
                                                            ) : (
                                                                <Avatar variant="rounded" sx={{ width: 45, height: 45, borderRadius: '10px', bgcolor: '#F4F7FE' }}><Notifications sx={{ color: '#A3AED0' }} /></Avatar>
                                                            )}
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1B2559' }}>{notif.title}</Typography>
                                                                <Typography variant="caption" sx={{ color: '#A3AED0' }}>{notif.body.substring(0, 45)}...</Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Total User Clicks">
                                                            <Box sx={{ bgcolor: '#FFF7ED', color: 'orange', px: 1.5, py: 0.5, borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                                                                <BarChart fontSize="small" /> {notif.click_count}
                                                            </Box>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: '#707EAE', fontSize: '0.85rem', ...poppins }}>
                                                        {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => downloadNotificationPDF(notif)}
                                                            sx={{ bgcolor: '#EEF2FF', mr: 1 }}
                                                        >
                                                            <CloudUpload fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* SUBSCRIBERS LOG MANAGEMENT LISTS */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white' }}>
                            <Box p={4} display="flex" alignItems="center" justifyContent="space-between">
                                <Box display="flex" alignItems="center" gap={2}>
                                    <People sx={{ color: 'orange' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>Subscribers</Typography>
                                </Box>
                                <Typography variant="caption" sx={{ bgcolor: '#F4F7FE', color: '#4318FF', px: 2, py: 0.5, borderRadius: '8px', fontWeight: 700 }}>
                                    {subscribers.length} Users
                                </Typography>
                            </Box>
                            <Divider />
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>IDENTITY</TableCell>
                                            <TableCell align="right" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>MANAGE</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {subscribers.map((sub) => (
                                            <TableRow key={sub.id} hover>
                                                <TableCell sx={poppins}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1B2559' }}>{sub.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{sub.email}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" sx={{ mr: 1 }}><Block fontSize="small" color={sub.banned ? "error" : "disabled"} /></IconButton>
                                                    <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* USER FEEDBACK DISCUSSION COMMENT GROUPS */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #E9EDF7', overflow: 'hidden', bgcolor: 'white' }}>
                            <Box p={4} display="flex" alignItems="center" gap={2}>
                                <CommentIcon sx={{ color: 'orange' }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B2559', ...poppins }}>User Feedback</Typography>
                            </Box>
                            <Divider />
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>CONTENT</TableCell>
                                            <TableCell align="right" sx={{ ...poppins, fontWeight: 600, bgcolor: '#F4F7FE' }}>ACTION</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {comments.map((com) => (
                                            <TableRow key={com.comment_id} hover>
                                                <TableCell sx={poppins}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1B2559' }}>{com.subscriber_name}</Typography>
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#707EAE' }}>{com.text}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" color="error"><Delete fontSize="small" /></IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>

                <Box sx={{ height: '100px' }} />
            </Container>

            {/* FIXED FOOTER CONTROLS */}
            <Box component="footer" sx={{
                position: 'fixed', bottom: 0, left: 0, right: 0, py: 2, px: 2,
                bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)',
                borderTop: '1px solid #E9EDF7', zIndex: 1000, ...poppins
            }}>
                <Container maxWidth="lg">
                    <Grid container alignItems="center" justifyContent="space-between">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Copyright sx={{ fontSize: 16, color: '#A3AED0' }} />
                                <Typography variant="caption" sx={{ color: '#707EAE', fontWeight: 500 }}>
                                    {new Date().getFullYear()} 237Showbiz Management.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{
                                bgcolor: '#F4F7FE', px: 2, py: 0.5, borderRadius: '20px', border: '1px solid #E9EDF7',
                                display: 'flex', alignItems: 'center', gap: 1.5
                            }}>
                                <Box sx={{ width: 8, height: 8, bgcolor: '#05CD99', borderRadius: '50%', boxShadow: '0 0 8px #05CD99' }} />
                                <Typography variant="caption" sx={{ color: '#1B2559', fontWeight: 700, fontSize: '0.75rem' }}>
                                    Active: {subscribers.length} Subscribers
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#A3AED0' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>v2.4.0</Typography>
                                <Favorite sx={{ fontSize: 12, color: '#EE5D50', ml: 1 }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
};

export default AdminPage;